import React, { FC, forwardRef, HTMLAttributes, ReactNode, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { TIcons } from '../../types/icons-type';
import Item from './Item';
import { useUserStore } from '../../stores/useUserStore';
import { getMenuByCategory, getVisibleMenuItems } from '../../utils/helpers';

interface IListProps extends HTMLAttributes<HTMLUListElement> {
	id?: string;
	children?: ReactNode;
	className?: string;
	ariaLabelledby?: string;
	parentId?: string;
	rootId?: string;
	horizontal?: boolean;
}
export const List = forwardRef<HTMLUListElement, IListProps>(
	({ id, children, className, ariaLabelledby, parentId, rootId, horizontal, ...props }, ref) => {
		return (
			<ul
				ref={ref}
				id={id}
				className={classNames('navigation', { 'navigation-menu': horizontal }, className)}
				aria-labelledby={ariaLabelledby}
				data-bs-parent={
					parentId === `${rootId}__${rootId}` ? `#${rootId}` : (parentId && `#${parentId}`) || null
				}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}>
				{children}
			</ul>
		);
	},
);
List.displayName = 'List';
List.propTypes = {
	id: PropTypes.string,
	children: PropTypes.node,
	className: PropTypes.string,
	ariaLabelledby: PropTypes.string,
	parentId: PropTypes.string,
	rootId: PropTypes.string,
	horizontal: PropTypes.bool,
};
List.defaultProps = {
	id: undefined,
	children: null,
	className: undefined,
	ariaLabelledby: undefined,
	parentId: undefined,
	rootId: undefined,
	horizontal: false,
};

interface INavigationLineProps {
	className?: string;
}
export const NavigationLine: FC<INavigationLineProps> = ({ className }) => {
	return <hr className={classNames('navigation-line', className)} />;
};
NavigationLine.propTypes = {
	className: PropTypes.string,
};
NavigationLine.defaultProps = {
	className: undefined,
};

interface INavigationTitleProps extends HTMLAttributes<HTMLSpanElement> {
	className?: string;
	children: ReactNode;
}
export const NavigationTitle: FC<INavigationTitleProps> = ({ className, children, ...props }) => {
	return (
		<li className='navigation-item'>
			{/* eslint-disable-next-line react/jsx-props-no-spreading */}
			<span className={classNames('navigation-title', className)} {...props}>
				{children}
			</span>
		</li>
	);
};
NavigationTitle.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
NavigationTitle.defaultProps = {
	className: undefined,
};

// Type per il menu item
interface IMenuItem {
	id?: string | number;
	text?: string;
	path?: string;
	icon?: TIcons;
	isDisable?: boolean;
	category?: string;
	hide?: boolean;
	adminHide?: boolean;
	notification?: any;
	subMenu?: {
		[key: string]: IMenuItem;
	} | null;
}

// Type per l'oggetto menu
interface IMenu {
	[key: string]: IMenuItem;
}

interface INavigationProps {
	horizontal?: boolean;
	menu: IMenu;
	id: string;
	className?: string;
	groupByCategory?: boolean;
	useSubmenu?: boolean;
}

const Navigation = forwardRef<HTMLElement, INavigationProps>(
	(
		{ menu, horizontal, id, className, groupByCategory = false, useSubmenu = false, ...props },
		ref,
	) => {
		const [activeItem, setActiveItem] = useState(undefined);
		const [userLogged] = useUserStore((state) => [state.user]);
		const { t } = useTranslation('menu');

		// Utilizza getVisibleMenuItems per filtrare i menu nascosti
		const visibleMenu = getVisibleMenuItems(menu, userLogged?.isClient, userLogged?.role);

		// Organizza per categoria se richiesto
		const processedMenu = groupByCategory ? getMenuByCategory(visibleMenu) : visibleMenu;

		const calculateText = (text: string) => {
			if (text === 'Agenzie' && userLogged?.isClient) {
				return 'Team leader';
			}
			return text;
		};

		const calculateIcon = (title: string, icon: TIcons) => {
			if (title === 'Agenzie' && userLogged?.isClient) {
				return 'SupervisedUserCircle';
			}
			return icon;
		};

		// Funzione per ottenere l'icona rappresentativa della categoria
		const getCategoryIcon = (category: string): TIcons => {
			// Mappa delle icone per categoria
			const categoryIcons: { [key: string]: TIcons } = {
				leads: 'People',
				templates: 'Description',
				management: 'ManageAccounts',
				reports: 'Assessment',
				communications: 'Campaign',
				settings: 'Settings',
				other: 'MoreHoriz',
			};

			return categoryIcons[category] || 'Folder';
		};

		function fillMenu(
			data: IMenu | { [category: string]: IMenu },
			parentId: string,
			rootId: string,
			isHorizontal: boolean | undefined,
			isMore: boolean | undefined,
			isCategoryMenu: boolean = false,
		) {
			// Se stiamo usando sottomenu basati su categorie
			if (useSubmenu && groupByCategory && !isMore && !isCategoryMenu) {
				// Creiamo un Item per ogni categoria, con i suoi elementi come subMenu
				return Object.keys(data).map((category) => {
					const categoryItems = data[category] as IMenu;
					const categoryId = `cat-${category}-${rootId}`;

					return (
						<Item
							key={categoryId}
							rootId={rootId}
							id={categoryId}
							title={t(category.charAt(0).toUpperCase() + category.slice(1)) as string}
							icon={getCategoryIcon(category)}
							parentId={parentId}
							isHorizontal={isHorizontal}
							setActiveItem={setActiveItem}
							activeItem={activeItem}>
							{fillMenu(categoryItems, categoryId, rootId, false, false, true)}
						</Item>
					);
				});
			}

			// Se stiamo raggruppando per categoria ma senza sottomenu
			if (
				groupByCategory &&
				!useSubmenu &&
				!isMore &&
				!parentId.includes('other-') &&
				!isCategoryMenu
			) {
				// Per ogni categoria, prima creiamo un titolo di navigazione e poi i relativi elementi
				return Object.keys(data).map((category) => {
					const categoryItems = data[category] as IMenu;
					return (
						<React.Fragment key={category}>
							<NavigationTitle>
								{t(category.charAt(0).toUpperCase() + category.slice(1)) as ReactNode}
							</NavigationTitle>
							{Object.keys(categoryItems).map((itemKey) => {
								const item = categoryItems[itemKey] as IMenuItem;
								return item.path ? (
									<Item
										key={item.id}
										rootId={rootId}
										id={item.id as any}
										title={calculateText(item.text as string)}
										icon={calculateIcon(item.text as string, item.icon as TIcons)}
										to={`${item.path}`}
										parentId={parentId}
										isHorizontal={isHorizontal}
										setActiveItem={setActiveItem}
										activeItem={activeItem}
										notification={item.notification}>
										{!!item.subMenu &&
											fillMenu(item.subMenu, item.id as string, rootId, isHorizontal, undefined)}
									</Item>
								) : null;
							})}
						</React.Fragment>
					);
				});
			}

			// Per il menu standard o per gli elementi all'interno di una categoria
			return Object.keys(data).map((item) => {
				const menuItem = data[item] as IMenuItem;
				return menuItem.path ? (
					<Item
						key={menuItem.id}
						rootId={rootId}
						id={menuItem.id as any}
						title={calculateText(menuItem.text as string)}
						icon={calculateIcon(menuItem.text as string, menuItem.icon as TIcons)}
						to={`${menuItem.path}`}
						parentId={parentId}
						isHorizontal={isHorizontal}
						setActiveItem={setActiveItem}
						activeItem={activeItem}
						notification={menuItem.notification}>
						{!!menuItem.subMenu &&
							fillMenu(menuItem.subMenu, menuItem.id as string, rootId, isHorizontal, undefined)}
					</Item>
				) : (
					!isMore && !isHorizontal && (
						<NavigationTitle key={menuItem.id as string}>
							{t(menuItem.text as string) as ReactNode}
						</NavigationTitle>
					)
				);
			});
		}

		return (
			// @ts-ignore
			// eslint-disable-next-line react/jsx-props-no-spreading
			<nav ref={ref} aria-label={id} className={className} {...props}>
				<List id={id} horizontal={horizontal}>
					{fillMenu(processedMenu, id, id, horizontal, undefined)}
					{horizontal && (
						<Item
							rootId={`other-${id}`}
							title={t('More') as string}
							icon='MoreHoriz'
							isHorizontal
							isMore>
							{fillMenu(visibleMenu, `other-${id}`, `other-${id}`, false, true)}
						</Item>
					)}
				</List>
			</nav>
		);
	},
);
Navigation.displayName = 'Navigation';
Navigation.propTypes = {
	horizontal: PropTypes.bool,
	// @ts-ignore
	menu: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		text: PropTypes.string,
		path: PropTypes.string,
		icon: PropTypes.string,
		isDisable: PropTypes.bool,
		category: PropTypes.string,
		hide: PropTypes.bool,
		subMenu: PropTypes.object,
	}).isRequired,
	id: PropTypes.string.isRequired,
	className: PropTypes.string,
	groupByCategory: PropTypes.bool,
	useSubmenu: PropTypes.bool,
};
Navigation.defaultProps = {
	horizontal: false,
	className: undefined,
	groupByCategory: false,
	useSubmenu: false,
};

export default Navigation;
